import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { BehaviorSubject, combineLatest, map } from "rxjs";
import { AppState } from "../store/app.state";
import * as ApplicationSelectors from "../store/application/application.selectors";
import * as TermLifeInsuranceQuoteSelectors from "../store/quote/term-life-insurance/quote.selectors";
import { NationalityFormService } from "./backend/nationality/nationality-form.service";
import { OccupationFormService } from "./backend/occupation/occupation-form.service";

@Injectable({
  providedIn: "root",
})
export class ExternalSystemCommunicationStateService {
  /**
   * For each service endpoint which we would like to call without our store we have to set the 'isBusy' flag manually.
   */
  private readonly _manuallySetBackendRequests$ =
    new BehaviorSubject<PendingBackendRequests>(
      DEFAULT_PENDING_BACKEND_REQUESTS,
    );

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _occupationFormService: OccupationFormService,
    private readonly _nationalityFormService: NationalityFormService,
  ) {}

  private readonly _hasAnyPendingQuoteBackendRequests$ = this._store.select(
    TermLifeInsuranceQuoteSelectors.selectIsBusy,
  );

  private readonly _hasAnyPendingApplicationBackendRequests$ =
    this._store.select(ApplicationSelectors.selectIsBusy);

  readonly hasAnyPendingBackendRequests$ = combineLatest([
    this._manuallySetBackendRequests$,
    this._hasAnyPendingQuoteBackendRequests$,
    this._hasAnyPendingApplicationBackendRequests$,
    this._occupationFormService.hasPendingRequests$,
    this._nationalityFormService.hasPendingRequests$,
  ]).pipe(
    map(
      ([
        manuallySetBackendRequests,
        hasAnyPendingLifeInsuranceBackendRequests,
        hasAnyPendingApplicationBackendRequests,
        hasAnyPendingOccupationBackendRequests,
        hasAnyPendingNationalityBackendRequests,
      ]) => {
        const hasAnyManuallySetBackendRequests =
          this.hasAnyPendingManuallySetBackendRequests(
            manuallySetBackendRequests,
          );

        return (
          hasAnyManuallySetBackendRequests ||
          (hasAnyPendingLifeInsuranceBackendRequests ?? false) ||
          (hasAnyPendingApplicationBackendRequests ?? false) ||
          hasAnyPendingOccupationBackendRequests ||
          hasAnyPendingNationalityBackendRequests
        );
      },
    ),
  );

  setPendingRequest(externalEndpoint: ExternalEndpoint) {
    const pendingBackendRequests = structuredClone(
      this._manuallySetBackendRequests$.value,
    );
    pendingBackendRequests[externalEndpoint] = true;
    this._manuallySetBackendRequests$.next(pendingBackendRequests);
  }

  unsetPendingRequest(externalEndpoint: ExternalEndpoint) {
    const pendingBackendRequests = structuredClone(
      this._manuallySetBackendRequests$.value,
    );
    pendingBackendRequests[externalEndpoint] = false;
    this._manuallySetBackendRequests$.next(pendingBackendRequests);
  }

  private hasAnyPendingManuallySetBackendRequests(
    manuallySetBackendRequests: PendingBackendRequests,
  ) {
    const hasAnyPendingManuallySetBackendRequests =
      manuallySetBackendRequests[ExternalEndpoint.QUOTES_GET_PRODUCT];

    return hasAnyPendingManuallySetBackendRequests;
  }
}

export interface PendingBackendRequests {
  // Quotes
  [ExternalEndpoint.QUOTES_GET_PRODUCT]: boolean;
}

export enum ExternalEndpoint {
  // Quotes
  QUOTES_GET_PRODUCT = "QUOTES_GET_PRODUCT",
}

const DEFAULT_PENDING_BACKEND_REQUESTS: PendingBackendRequests = {
  [ExternalEndpoint.QUOTES_GET_PRODUCT]: false,
};
